import React, { useEffect, useState } from "react";
import {
  collection,
  query,
  onSnapshot,
  orderBy,
  getCountFromServer,
  Timestamp,
} from "firebase/firestore";
import { Search, ShoppingCart, DollarSign, Package, TrendingUp } from "lucide-react";
import Header from "../Components/Header";
import { db } from "../firebase/config";

// Stat Card Component with themed colors
const StatCard = ({
  icon: Icon,
  title,
  value,
  iconColor = "bg-primaryBlue/20",
  textColor = "text-primaryBlue",
}) => (
  <div className="bg-white/10 backdrop-blur-sm rounded-sm border border-[#fefefe20] p-4 hover:shadow-lg transition-shadow duration-200">
    <div className="flex items-center space-x-4">
      <div className={`p-3 rounded-full ${iconColor}`}>
        <Icon className={`w-6 h-6 ${textColor}`} />
      </div>
      <div>
        <p className="text-sm font-medium text-white/60">{title}</p>
        <h3 className="text-2xl text-left font-bold text-white">{value}</h3>
      </div>
    </div>
  </div>
);

// Pagination Component
const Pagination = ({ currentPage, totalPages, onPageChange }) => (
  <div className="flex items-center justify-center px-6 py-4 bg-primary opacity-75 border-t border-[#fefefe20]">
    <button
      onClick={() => onPageChange(currentPage - 1)}
      disabled={currentPage === 1}
      className="px-4 py-2 text-xl font-medium text-white/60 disabled:opacity-50 disabled:cursor-not-allowed hover:text-white transition-colors"
    >
      &lt;
    </button>
    <span className="text-sm text-white/60">
      Page {currentPage} of {totalPages}
    </span>
    <button
      onClick={() => onPageChange(currentPage + 1)}
      disabled={currentPage === totalPages}
      className="px-4 py-2 text-xl font-medium text-white/60 disabled:opacity-50 disabled:cursor-not-allowed hover:text-white transition-colors"
    >
      &gt;
    </button>
  </div>
);

// Main Dashboard Component
const ProductPurchaseTracking = ({ setIsSidebarOpen, isSidebarOpen }) => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalPurchases: 0,
    totalRevenue: 0,
    starterPurchases: 0,
    averageOrderValue: 0,
  });
  const [purchases, setPurchases] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 8;

  useEffect(() => {
    let unsubscribe;
    const fetchPurchaseData = async () => {
      try {
        setLoading(true);

        // Get total count
        const totalCount = (
          await getCountFromServer(query(collection(db, "productPurchase")))
        ).data().count;

        // Set up real-time listener
        const q = query(
          collection(db, "productPurchase"),
          orderBy("createdAt", "desc")
        );

        unsubscribe = onSnapshot(q, (querySnapshot) => {
          const purchaseData = querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));


          // Calculate stats
          const totalRevenue = purchaseData.reduce(
            (sum, purchase) => sum + (purchase.price || 0),
            0
          );
          const starterPurchases = purchaseData.filter(
            (p) => p.selectedTier === "Starter"
          ).length;
          const averageOrderValue =
            totalCount > 0 ? Math.round(totalRevenue / totalCount) : 0;

          setStats({
            totalPurchases: totalCount,
            totalRevenue: totalRevenue,
            starterPurchases: starterPurchases,
            averageOrderValue: averageOrderValue,
          });

          setPurchases(purchaseData);
          setLoading(false);
        });
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchPurchaseData();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const filteredPurchases = purchases.filter(
    (purchase) =>
      (purchase.name?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
      (purchase.email?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
      (purchase.purchaseId?.toLowerCase() || "").includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredPurchases.length / recordsPerPage);
  const startIndex = (currentPage - 1) * recordsPerPage;
  const paginatedPurchases = filteredPurchases.slice(
    startIndex,
    startIndex + recordsPerPage
  );

  const handlePageChange = (newPage) => setCurrentPage(newPage);

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    try {
      if (timestamp.toDate) {
        return timestamp.toDate().toLocaleString();
      }
      return new Date(timestamp).toLocaleString();
    } catch (error) {
      return "Invalid Date";
    }
  };

  return (
    <div className="rounded-sm font-poppins">
      <div className="w-[100%]">
        <Header
          PageName={"Product Purchases"}
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
        />
      </div>
      <div className="mx-auto space-y-4">
        {loading ? (
          <div className="rounded-sm border-[#ffffff55] border h-[90vh] flex justify-center items-center bg-primary opacity-75 font-poppins">
            <div className="animate-spin w-16 h-16 border-4 border-primaryBlue border-t-transparent rounded-full" />
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                icon={ShoppingCart}
                title="Total Purchases"
                value={stats.totalPurchases}
                iconColor="bg-purple-500/20"
                textColor="text-purple-500"
              />
              <StatCard
                icon={DollarSign}
                title="Total Revenue"
                value={`$${stats.totalRevenue}`}
                iconColor="bg-green-500/20"
                textColor="text-green-500"
              />
              <StatCard
                icon={Package}
                title="Starter Tier Purchases"
                value={stats.starterPurchases}
                iconColor="bg-blue-500/20"
                textColor="text-blue-500"
              />
              <StatCard
                icon={TrendingUp}
                title="Avg. Order Value"
                value={`$${stats.averageOrderValue}`}
                iconColor="bg-yellow-500/20"
                textColor="text-yellow-500"
              />
            </div>

            {/* Purchases Table */}
            <div className="bg-primary opacity-75 rounded-sm overflow-hidden">
              <div className="p-3 px-4 border-b header-container border-[#fefefe20]">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-semibold text-white">
                    Purchase Details
                  </h2>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="pl-10 pr-4 py-2 bg-white/5 border border-borderColor rounded-sm text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-primaryBlue/50"
                    />
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/40 w-4 h-4" />
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-rounded-sm scrollbar-thumb-blue-500">
                <table className="w-full">
                  <thead className="bg-white/5">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-white/60 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-white/60 uppercase tracking-wider">
                        Phone
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-white/60 uppercase tracking-wider">
                        Purchase ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-white/60 uppercase tracking-wider">
                        Tier
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-white/60 uppercase tracking-wider">
                        Price
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-white/60 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-white/60 uppercase tracking-wider">
                        Purchase Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-white/60 uppercase tracking-wider">
                        Created At
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#f3f3f318]">
                    {paginatedPurchases.map((purchase) => (
                      <tr key={purchase.id} className="hover:bg-white/5">
                       
                        <td className="px-6 py-4 text-left whitespace-nowrap">
                          <div className="text-sm text-white/60">
                            {purchase.email || "N/A"}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-left whitespace-nowrap">
                          <div className="text-sm text-white/60">
                            {purchase.phone || "N/A"}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-left whitespace-nowrap">
                          <div className="text-sm text-white/60 font-mono">
                            {purchase.purchaseId?.substring(0, 12) || "N/A"}...
                          </div>
                        </td>
                        <td className="px-6 py-4 text-left whitespace-nowrap">
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-500/20 text-blue-400">
                            {purchase.tierName || "N/A"}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-left whitespace-nowrap">
                          <div className="text-sm font-medium text-green-400">
                            ${purchase.price || 0}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-left whitespace-nowrap">
                          <span
                            className={`px-2 py-1 text-xs font-semibold rounded-full ${
                              purchase.status === "confirmed"
                                ? "bg-green-500/20 text-green-400"
                                : "bg-yellow-500/20 text-yellow-400"
                            }`}
                          >
                            {purchase.status || "pending"}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-left whitespace-nowrap">
                          <div className="text-sm text-white/60">
                            {formatDate(purchase.purchaseDate)}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-left whitespace-nowrap">
                          <div className="text-sm text-white/60">
                            {formatDate(purchase.createdAt)}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProductPurchaseTracking;